import { FC } from 'react';
import { Add, Close, Save, Article, DateRange, Contacts } from '@mui/icons-material';
import { Grid, Box, ButtonGroup, Button, Divider, Typography, MenuItem } from '@mui/material';
import { useQuery } from 'react-query';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  putContractInfo,
  getJobSupervisors,
  getJobProjectManagers,
  getFramersLookup
} from '../../../../fetch';
import {
  IContractInfo,
  IContractInfoReadOnly,
  IJobSupervisors,
  IJobProjectManagers
} from '../../../../models';
import { Loader, Card, SMBSelect } from '../../../../components';
import { formatDate, formatMoney, formatDBFriendlyDateTime } from '../../../../helpers';
import { DateTimePicker, DatePicker } from '@mui/x-date-pickers';

interface IContractInformation {
  contractInfo: IContractInfo;
  isLoadingContractInfo: boolean;
  contractInfoReadOnly: IContractInfoReadOnly;
  isLoadingContractInfoReadOnly: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchContractInfo: () => void;
  refetchContractInfoReadOnly: () => void;
}

const ContractInfoSchema = Yup.object().shape({
  lotCost: Yup.number()
    .typeError('Lot Cost must be a number')
    .max(200000, 'Lot Cost must be less than 200,000')
});

export const ContractInfo: FC<IContractInformation> = ({
  contractInfo,
  isLoadingContractInfo,
  contractInfoReadOnly,
  isLoadingContractInfoReadOnly,
  setDirtyForm,
  refetchContractInfo,
  refetchContractInfoReadOnly
}) => {
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { jobId }: { jobId: string } = useParams();

  const { isLoading: isLoadingProjectManagers, data: projectManagers } = useQuery<
    IJobProjectManagers[],
    Error
  >(['getJobProjectManagers'], () => getJobProjectManagers(jobId));

  const { isLoading: isLoadingSupervisors, data: supervisors } = useQuery<IJobSupervisors[], Error>(
    ['getJobSupervisors'],
    () => getJobSupervisors(jobId)
  );

  const { isLoading: isLoadingFramers, data: framers } = useQuery<any[], Error>(
    ['getFramersLookup'],
    () => getFramersLookup()
  );

  return (
    <>
      {(isLoadingContractInfo || isLoadingContractInfoReadOnly) && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {(!isLoadingContractInfo || !isLoadingContractInfoReadOnly) && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...contractInfo,
            sageClosingDate: contractInfo?.sageClosingDate
              ? new Date(contractInfo?.sageClosingDate)
              : null,
            closingAppointment: contractInfo?.closingAppointment
              ? new Date(contractInfo?.closingAppointment)
              : null,
            walkthroughDate: contractInfo?.walkthroughDate
              ? new Date(contractInfo?.walkthroughDate)
              : null,
            rewalkDate: contractInfo?.rewalkDate ? new Date(contractInfo?.rewalkDate) : null,
            formDate: contractInfo?.formDate ? new Date(contractInfo?.formDate) : null,
            startDate: contractInfo?.startDate ? new Date(contractInfo?.startDate) : null,
            estimatedCompletionDate: contractInfo?.estimatedCompletionDate
              ? new Date(contractInfo?.estimatedCompletionDate)
              : null,
            projectManagerId: contractInfo?.projectManagerId ?? '',
            supervisorId: contractInfo?.supervisorId ?? '',
            framerId: contractInfo?.framerId ?? '',
            basePrice: contractInfoReadOnly?.basePrice
              ? formatMoney(contractInfoReadOnly?.basePrice, 2)
              : '',
            lotPremium: contractInfoReadOnly?.lotPremium
              ? formatMoney(contractInfoReadOnly?.lotPremium, 2)
              : '',
            upgradePrice: contractInfoReadOnly?.upgradePrice
              ? formatMoney(contractInfoReadOnly?.upgradePrice, 2)
              : '',
            totalPrice: contractInfoReadOnly?.totalPrice
              ? formatMoney(contractInfoReadOnly?.totalPrice, 2)
              : '',
            contractDate: contractInfoReadOnly?.contractDate
              ? formatDate(contractInfoReadOnly?.contractDate)
              : ''
          }}
          validationSchema={ContractInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IContractInfo = {
                ...values,
                sageClosingDate: formatDBFriendlyDateTime(values?.sageClosingDate),
                closingAppointment: formatDBFriendlyDateTime(values?.closingAppointment),
                walkthroughDate: formatDBFriendlyDateTime(values?.walkthroughDate),
                rewalkDate: formatDBFriendlyDateTime(values?.rewalkDate),
                formDate: formatDBFriendlyDateTime(values?.formDate),
                startDate: formatDBFriendlyDateTime(values?.startDate),
                estimatedCompletionDate: formatDBFriendlyDateTime(values?.estimatedCompletionDate)
              };

              await putContractInfo(parseInt(jobId), data);
              refetchContractInfo();
              refetchContractInfoReadOnly();
              enqueueSnackbar('Contract info has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Job was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Card title="Contract Info" icon={<Article />}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid container spacing={0.5}>
                            <Grid item xs={12}>
                              <Typography className={classes.readOnlyHeaders} variant="h6">
                                Options
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Selected Floor Plan:</span>
                                {contractInfoReadOnly?.planDescription ? (
                                  contractInfoReadOnly?.planDescription
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>
                                  Structural Options Selected:
                                </span>
                                {contractInfoReadOnly?.selectedStructuralOptions?.length > 0 ? (
                                  contractInfoReadOnly?.selectedStructuralOptions.map(
                                    (structuralOption, index) => {
                                      return (
                                        <Typography variant="subtitle1">
                                          - {structuralOption}
                                        </Typography>
                                      );
                                    }
                                  )
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography className={classes.readOnlyHeaders} variant="h6">
                                Agents
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Listing Agent:</span>
                                {contractInfoReadOnly?.listingAgent ? (
                                  contractInfoReadOnly?.listingAgent
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Mortgage Lender:</span>
                                {contractInfoReadOnly?.mortgageLender ? (
                                  contractInfoReadOnly?.mortgageLender
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography className={classes.readOnlyHeaders} variant="h6">
                                Costs
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Base Price:</span>
                                {values?.basePrice ? (
                                  values?.basePrice
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Lot Premium:</span>
                                {values?.lotPremium ? (
                                  values?.lotPremium
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Price of Upgrades:</span>
                                {values?.upgradePrice ? (
                                  values?.upgradePrice
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Total Price:</span>
                                {values?.totalPrice ? (
                                  values?.totalPrice
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="subtitle1">
                                <span className={classes.contractInfo}>Contract Date:</span>
                                {values?.contractDate ? (
                                  values?.contractDate
                                ) : (
                                  <span className={classes.notSelected}>Not Selected</span>
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                    <Card title="Dates" icon={<DateRange />}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <Box mt={1}>
                                <DateTimePicker
                                  label="Estimated Close Date"
                                  timeSteps={{ minutes: 1 }}
                                  onChange={(date: any) => {
                                    setFieldValue('sageClosingDate', date);
                                  }}
                                  maxDate={values.sageClosingDate}
                                  value={values.sageClosingDate}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      value: values.sageClosingDate
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box mt={1}>
                                <DateTimePicker
                                  label="Actual Close Date"
                                  timeSteps={{ minutes: 1 }}
                                  onChange={(date: any) => {
                                    setFieldValue('closingAppointment', date);
                                  }}
                                  maxDate={values.closingAppointment}
                                  value={values.closingAppointment}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      value: values.closingAppointment
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box mt={1}>
                                <DatePicker
                                  label="Walkthrough Date"
                                  onChange={(date: any) => {
                                    setFieldValue('walkthroughDate', date);
                                  }}
                                  maxDate={values.walkthroughDate}
                                  value={values.walkthroughDate}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      value: values.walkthroughDate
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box mt={1}>
                                <DatePicker
                                  label="Rewalk Date"
                                  onChange={(date: any) => {
                                    setFieldValue('rewalkDate', date);
                                  }}
                                  maxDate={values.rewalkDate}
                                  value={values.rewalkDate}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      value: values.rewalkDate
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box mt={1}>
                                <DatePicker
                                  label="Form Date"
                                  onChange={(date: any) => {
                                    setFieldValue('formDate', date);
                                  }}
                                  maxDate={values.formDate}
                                  value={values.formDate}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      value: values.formDate
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box mt={1}>
                                <DatePicker
                                  label="Start Date"
                                  onChange={(date: any) => {
                                    setFieldValue('startDate', date);
                                  }}
                                  maxDate={values.startDate}
                                  value={values.startDate}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      value: values.startDate
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box mt={1}>
                                <DatePicker
                                  label="Estimated Completion Date"
                                  onChange={(date: any) => {
                                    setFieldValue('estimatedCompletionDate', date);
                                  }}
                                  maxDate={values.estimatedCompletionDate}
                                  value={values.estimatedCompletionDate}
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      value: values.estimatedCompletionDate
                                    }
                                  }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                    <Card title="Contacts" icon={<Contacts />}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12} sm={4}>
                              <SMBSelect
                                name="projectManagerId"
                                label="Project Manager"
                                id="projectManagerId"
                                variant="outlined"
                                disabled={isLoadingProjectManagers}
                                value={values.projectManagerId}
                                onChange={({ target: { value } }) => {
                                  setFieldValue('projectManagerId', value);
                                }}
                                onBlur={handleBlur}
                              >
                                {projectManagers &&
                                  projectManagers.map((manager, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={manager.projectManagerId}>
                                        {manager.name}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <SMBSelect
                                name="supervisorId"
                                label="Supervisor"
                                id="supervisorId"
                                variant="outlined"
                                disabled={isLoadingSupervisors}
                                value={values.supervisorId}
                                onChange={({ target: { value } }) => {
                                  setFieldValue('supervisorId', value);
                                }}
                                onBlur={handleBlur}
                              >
                                {supervisors &&
                                  supervisors.map((supervisor, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={supervisor.supervisorId}>
                                        {supervisor.name}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <SMBSelect
                                name="framerId"
                                label="Framers"
                                id="framerId"
                                variant="outlined"
                                disabled={isLoadingFramers}
                                value={values.framerId}
                                onChange={({ target: { value } }) => {
                                  setFieldValue('framerId', value);
                                }}
                                onBlur={handleBlur}
                              >
                                {framers &&
                                  framers.map((framer, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={framer.framerId}>
                                        {framer.name}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={jobId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/jobs');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginTop: theme.spacing(1)
  },
  contractInfo: {
    fontWeight: '500',
    paddingRight: 8
  },
  readOnlyHeaders: {
    paddingBottom: theme.spacing(0.25),
    fontWeight: '700'
  },
  notSelected: {
    color: '#88888',
    opacity: 0.6
  }
}));
